import { Component, OnInit, OnDestroy, Input, Inject, Output, EventEmitter } from '@angular/core';
import { DeviceService, GlobalEventBusService, IDeviceModel } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import { BigInteger, ISystemInfo, SystemInfo } from 'app/modules/remote-control-ng/definitions';
import { ISystemInfoUI } from 'app/modules/remote-control-ng';
import { RcContext } from 'app/modules/remote-control-ng/context/rcContext';
import { GetSysInfoMessage } from 'app/modules/remote-control-ng/messages/pocket-com';
import { AppUIService } from 'app/modules/remote-control-ng/services/appUIService';
import { OsDetectionService } from 'app/modules/remote-control-ng/services/osDetectionService';
import { MathHelpers } from 'app/modules/remote-control-ng/utils/math';
import { RGB, rgb2hex } from 'app/modules/remote-control/services/color';
import { BehaviorSubject, Subscription } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { XsI18nPipe } from '@nx-fe/pipes';
import { Toast, ToastTypeIndex } from 'app/shared/components/toast/toast.component';
import { SnapshotService } from 'app/modules/ticket/services/snapshot.service';

interface ISysInfoDisplay {
  width: number;
  height: number;
  depth: number;
}

interface ISysInfoSpace {
  total: string;
  used: string;
  free: string;
  usedNumeric: BigInteger;
}

interface ISysInfoBattery {
  value: string;
  status: string;
  temperature: string;
}

interface ISysInfoDeviceDetails {
  deviceId: string;
  deviceName: string;
  osVersion: string;
  platform: string;
  macAddress: string;
  manufacturer: string;
  model: string;
  phoneNumber: string;
  hardwareSerialNumber: string;
  userName: string;
  userPhoneNumber: string;
  mcVersion: string;
  imei: string;
}

@Component({
  selector: 'xs-device-info',
  templateUrl: './device-info.component.html',
  styleUrls: ['./device-info.component.scss'],
})
export class DeviceInfoComponent implements OnInit, OnDestroy, ISystemInfoUI {
  private deviceSubj = new BehaviorSubject<IDeviceModel>(null);

  @Input()
  get device(): IDeviceModel {
    return this.deviceSubj.value;
  }
  set device(device: IDeviceModel) {
    this.deviceSubj.next(device);
  }

  @Output() setToast = new EventEmitter<Toast>();

  public display: ISysInfoDisplay;
  public deviceInfo: ISysInfoDeviceDetails;
  public physicalMemory: ISysInfoSpace;
  public virtualMemory: ISysInfoSpace;
  public storage: ISysInfoSpace;
  public battery: ISysInfoBattery;
  public currentSystemInfo: any;
  private associationBarMinimized = true;
  private associationBarMinimizedSub: Subscription;
  private associationBarRestoredSub: Subscription;
  private isInitialRefresh: boolean = true;

  constructor(
    private deviceService: DeviceService,
    private globalEventBusService: GlobalEventBusService,
    private appUI: AppUIService,
    private saI18nPipe: XsI18nPipe,
    private snapshotService: SnapshotService
  ) {
    this.appUI.setSystemInfoUI(this);
  }

  ngOnInit() {
    this.checkPermissions();
    $('#systeminfo').hide();

    this.deviceInfo = {
      deviceId: RcContext.instance.deviceId,
      deviceName: '',
      osVersion: '',
      platform: '',
      macAddress: '',
      manufacturer: '',
      model: '',
      phoneNumber: '',
      hardwareSerialNumber: '',
      userName: '',
      userPhoneNumber: '',
      mcVersion: '',
      imei: '',
    };

    this.associationBarMinimizedSub = this.globalEventBusService.register(GlobalEvents.associationBarMinimized, () => {
      this.associationBarMinimized = true;
    });
    this.associationBarRestoredSub = this.globalEventBusService.register(GlobalEvents.associationBarRestored, () => {
      this.associationBarMinimized = false;
    });
    this.globalEventBusService.register(GlobalEvents.remoteControlStarted, () => {
      this.checkPermissions();
    });
  }

  public ngOnDestroy = () => {
    if (this.associationBarMinimizedSub) {
      this.associationBarMinimizedSub.unsubscribe();
    }
    if (this.associationBarRestoredSub) {
      this.associationBarRestoredSub.unsubscribe();
    }
  };

  public refreshInfo = () => {
    if (!this.snapshotService.fetchingSnapshot) {
      this.displayToastMessage('snapshot_getting_latest_info_message', ToastTypeIndex.info);
      this.snapshotService.fetchingSnapshot = true;
      RcContext.instance.session.sendCallbackRequest(new GetSysInfoMessage());
    }
  };

  public getProperDisplayValue = (value: string): string => {
    if (value) {
      return value;
    }

    return 'n/a';
  };
  public update = (systemInfo: ISystemInfo) => {
    this.currentSystemInfo = systemInfo;
    this.display = {
      width: systemInfo.main.scrWidth,
      height: systemInfo.main.scrHeight,
      depth: systemInfo.main.bitsPerPixel,
    };

    this.deviceSubj
      .pipe(
        filter(device => !!device),
        first(),
        switchMap(device => this.deviceService.getDevice(device.deviceId, true)),
        filter(device => !!device),
        first()
      )
      .subscribe(device => this.prepareDeviceInfo(device));

    this.battery = undefined;

    this.physicalMemory = {
      free: MathHelpers.toSizeString(systemInfo.main.freeMemory),
      used: MathHelpers.toSizeString(systemInfo.main.totalMemory.subtract(systemInfo.main.freeMemory)),
      total: MathHelpers.toSizeString(systemInfo.main.totalMemory),
      usedNumeric: systemInfo.main.totalMemory.subtract(systemInfo.main.freeMemory),
    };

    this.virtualMemory = {
      free: MathHelpers.toSizeString(systemInfo.main.freeVirtualMemory),
      used: MathHelpers.toSizeString(
        systemInfo.main.totalVirtualMemory.subtract(systemInfo.main.freeVirtualMemory)
      ),
      total: MathHelpers.toSizeString(systemInfo.main.totalVirtualMemory),
      usedNumeric: systemInfo.main.totalVirtualMemory.subtract(systemInfo.main.freeVirtualMemory),
    };

    this.storage = {
      free: MathHelpers.toSizeString(systemInfo.main.freeStorage),
      used: MathHelpers.toSizeString(systemInfo.main.totalStorage.subtract(systemInfo.main.freeStorage)),
      total: MathHelpers.toSizeString(systemInfo.main.totalStorage),
      usedNumeric: systemInfo.main.totalStorage.subtract(systemInfo.main.freeStorage),
    };
    const $batteryTempDiv = $('#sysinfo-battery-temp-div');
    const $batteryStatusDiv = $('#sysinfo-battery-status-div');

    const $backupBatteryTempDiv = $('#sysinfo-backup-battery-temp-div');
    const $backupBatteryStatusDiv = $('#sysinfo-backup-battery-status-div');

    $('#sysinfo-vmem-section-div').css('display', 'none');
    $('#sysinfo-vmem-section-hr').css('display', 'none');

    $('#sysinfo-instorage-section-div').css('display', 'none');
    $('#sysinfo-instorage-section-hr').css('display', 'none');

    if (OsDetectionService.IsAndroid(systemInfo.main.osType)) {
      let curBatteryStatusLabel = 'unknown';

      switch ((<any>systemInfo.platformSpecific).batteryStatus) {
        case SystemInfo.BatteryStatusAndroid.Charging:
          curBatteryStatusLabel = 'charging';
          break;
        case SystemInfo.BatteryStatusAndroid.Discharging:
          curBatteryStatusLabel = 'discharging';
          break;
        case SystemInfo.BatteryStatusAndroid.NotCharging:
          curBatteryStatusLabel = 'notCharging';
          break;
        case SystemInfo.BatteryStatusAndroid.Full:
          curBatteryStatusLabel = 'full';
          break;
      }
      if (
        systemInfo.main.freeVirtualMemory < systemInfo.main.totalVirtualMemory &&
        systemInfo.main.totalVirtualMemory.greater(0) &&
        systemInfo.main.freeVirtualMemory.greater(0)
      ) {
        $('#sysinfo-instorage-section-div').css('display', 'block');
        $('#sysinfo-instorage-section-hr').css('display', 'block');
      }

      this.battery = {
        value: (<any>systemInfo.platformSpecific).batteryLevel,
        temperature: (<any>systemInfo.platformSpecific).batteryTemperature / 10 + '°C',
        status: curBatteryStatusLabel,
      };
    }

    if (
      OsDetectionService.IsWindowsCe(systemInfo.main.osType) ||
      OsDetectionService.IsWindows(systemInfo.main.osType) ||
      OsDetectionService.IsUniversalWindows(systemInfo.main.osType)
    ) {
      if ((<any>systemInfo.platformSpecific).batteryLifePercent !== undefined) {
        this.battery = {
          value: (<any>systemInfo.platformSpecific).batteryLifePercent,
          temperature: '',
          status: '',
        };
      }

      if (
        (<any>systemInfo.platformSpecific).batteryFlag <= SystemInfo.BatteryStatusWM.Charging &&
        (<any>systemInfo.platformSpecific).batteryLifePercent <= 100
      ) {
        $batteryTempDiv.css('display', 'none');
        $batteryStatusDiv.css('display', 'none');
        const $batterySectionDiv = $('#sysinfo-battery-section-div');
        $batterySectionDiv.css('display', 'block');
      } else {
        const $batterySectionDiv = $('#sysinfo-battery-section-div');
        $batterySectionDiv.css('display', 'none');
      }

      if (
        (<any>systemInfo.platformSpecific).backupBatteryFlag <= SystemInfo.BatteryStatusWM.Charging &&
        (<any>systemInfo.platformSpecific).backupBatteryLifePercent <= 100
      ) {
        $backupBatteryTempDiv.css('display', 'none');
        $backupBatteryStatusDiv.css('display', 'none');
        const $batterySectionDiv = $('#sysinfo-backup-battery-section-div');
        $batterySectionDiv.css('display', 'block');
      } else {
        const $batterySectionDiv = $('#sysinfo-backup-battery-section-div');
        $batterySectionDiv.css('display', 'none');
      }
    }

    if (OsDetectionService.IsIOS(systemInfo.main.osType)) {
      this.battery = {
        value: (<any>systemInfo.platformSpecific).batteryLevel,
        temperature: '',
        status: '',
      };
      $batteryTempDiv.css('display', 'none');
      $batteryStatusDiv.css('display', 'none');

      if (systemInfo.main.totalStorage.equals(0)) {
        $('#sysinfo-storage-section-div').css('display', 'none');
      }
    } else {
      if (
        OsDetectionService.IsWindowsCe(systemInfo.main.osType) &&
        systemInfo.main.freeVirtualMemory < systemInfo.main.totalVirtualMemory &&
        systemInfo.main.totalVirtualMemory.greater(0) &&
        systemInfo.main.freeVirtualMemory.greater(0)
      ) {
        $('#sysinfo-vmem-section-div').css('display', 'block');
        $('#sysinfo-vmem-section-hr').css('display', 'block');
      }
    }
    if (this.battery === undefined || parseInt(this.battery.value, 10) > 100 || parseInt(this.battery.value, 10) < 0) {
      $('#sysinfo-battery-section-div').css('display', 'none');
    } else {
      $('#sysinfo-battery-section-div').css('display', 'block');
    }
    $('#systeminfo').show();

    setTimeout(() => {
      if (this.snapshotService.fetchingSnapshot) {
        this.snapshotService.setSnapshotLastRefreshed(this.device.deviceId, new Date());
        this.snapshotService.fetchingSnapshot = false;
        this.displayToastMessage('snapshot_getting_latest_success_message', ToastTypeIndex.success);
      }
      this.isInitialRefresh = false;
    });
  };

  public getVirtualMemoryValues = () => {
    if (this.currentSystemInfo === undefined) {
      return '';
    }

    const used = this.currentSystemInfo.main.totalVirtualMemory
      .subtract(this.currentSystemInfo.main.freeVirtualMemory)
      .divide(1048576)
      .toString();
    const free = this.currentSystemInfo.main.freeVirtualMemory.divide(1048576).toString();
    return [used, free].toString();
  };

  public getMainMemoryValues = () => {
    if (this.currentSystemInfo === undefined) {
      return '';
    }

    const used = this.currentSystemInfo.main.totalMemory
      .subtract(this.currentSystemInfo.main.freeMemory)
      .divide(1048576)
      .toString();
    const free = this.currentSystemInfo.main.freeMemory.divide(1048576).toString();
    return [used, free].toString();
  };

  public getStorageValues = () => {
    if (this.currentSystemInfo === undefined) {
      return '';
    }

    const used = this.currentSystemInfo.main.totalStorage
      .subtract(this.currentSystemInfo.main.freeStorage)
      .divide(1048576)
      .toString();
    const free = this.currentSystemInfo.main.freeStorage.divide(1048576).toString();
    return [used, free].toString();
  };

  public getColors = () => {
    let colorUsed = $('.sysinfo-div-box-used').css('background-color');
    let colorFree = $('.sysinfo-div-box-free').css('background-color');
    if (colorUsed === undefined || colorFree === undefined) {
      return '';
    }

    if (colorUsed.charAt(0) !== '#') {
      colorUsed = this.rgb2hex(colorUsed);
    }

    if (colorFree.charAt(0) !== '#') {
      colorFree = this.rgb2hex(colorFree);
    }

    return [colorUsed, colorFree].toString();
  };

  public descriptionBootstrapClass = (): string => {
    return this.associationBarMinimized
      ? 'col-lg-6 col-md-6 col-sm-12 top-buffer-md'
      : 'col-lg-6 col-md-12 col-sm-12 top-buffer-md';
  };

  private displayToastMessage = (messageKey: string, type: ToastTypeIndex) => {
    if (!this.isInitialRefresh) {
      this.setToast.emit({ message: this.saI18nPipe.transform(messageKey), type } as Toast);
    }
  };

  private rgb2hex = (rgb: string): string => {
    let regex = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (regex === null) {
      regex = rgb.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*(\d+)\)$/);
      if (regex !== null) {
        return '#000000'; // not a color .. return black
      } else {
        return '#000000'; // returning black for invalid input
      }
    }

    const color = {
      r: parseInt(regex[1], 10),
      g: parseInt(regex[2], 10),
      b: parseInt(regex[3], 10),
    } as RGB;
    return rgb2hex(color).toLowerCase();
  };

  private checkPermissions = () => {
    // if (this.securityContext.RemoteControlViewSystemInfo !== this.tabViewController.enabled) {
    //     this.tabViewController.enabled = this.securityContext.RemoteControlViewSystemInfo;
    //     this.$timeout();
    // }
  };

  private prepareDeviceInfo = (device: IDeviceModel) => {
    this.device = device;
    this.deviceInfo.deviceId = device.deviceId;
    this.deviceInfo.deviceName = device.deviceName;
    this.deviceInfo.osVersion = device.osVersion;
    this.deviceInfo.platform = device.family;
    this.deviceInfo.macAddress = device.macAddress;
    this.deviceInfo.manufacturer = device.manufacturer;
    this.deviceInfo.model = device.model;
    this.deviceInfo.phoneNumber = device.phoneNumber;
    this.deviceInfo.hardwareSerialNumber = device.hardwareSerialNumber;
    this.deviceInfo.userName = device.userName;
    this.deviceInfo.userPhoneNumber = device.userPhoneNumber;
    this.deviceInfo.imei = device.imei;
    $('#sysinfo-phonenumber-section-div').css(
      'display',
      this.deviceInfo.phoneNumber === '' || !this.deviceInfo.phoneNumber ? 'none' : 'table'
    );
    $('#sysinfo-username-section-div').css(
      'display',
      this.deviceInfo.userName === '' || !this.deviceInfo.userName ? 'none' : 'table'
    );
    $('#sysinfo-userPhoneNumber-section-div').css(
      'display',
      this.deviceInfo.userPhoneNumber === '' || !this.deviceInfo.userPhoneNumber ? 'none' : 'table'
    );
    $('#sysinfo-model-section-div').css(
      'display',
      this.deviceInfo.model === '' || !this.deviceInfo.model ? 'none' : 'table'
    );
    $('#sysinfo-serialnumber-section-div').css(
      'display',
      this.deviceInfo.hardwareSerialNumber === '' || !this.deviceInfo.hardwareSerialNumber ? 'none' : 'table'
    );
    $('#sysinfo-imei-section-div').css(
      'display',
      this.deviceInfo.imei === '' || !this.deviceInfo.imei ? 'none' : 'table'
    );
  };
}
